import React, { useEffect, useRef } from 'react';
import { X, Loader2, Feather } from 'lucide-react';

const PoemEditorModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  title,
  setTitle,
  text, 
  setText, 
  author, 
  setAuthor,
  date,
  setDate,
  tags,
  setTags,
  isSaving, 
  isDarkMode,
  mode = 'add' 
}) => {
  
  const textareaRef = useRef(null);

  useEffect(() => {
    const handleEsc = (e) => { if (e.keyCode === 27) onClose(); };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Auto-resize the textarea as the user types
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  // Default to today's date when opening the modal for a new poem
  useEffect(() => {
    if (isOpen && mode === 'add' && !date) {
      setDate(new Date().toISOString().split('T')[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode]);

  const handleTextChange = (e) => {
    const target = e.target;
    const val = target.value;
    const selectionStart = target.selectionStart;

    if (val.includes('--')) {
      const beforeCursor = val.substring(0, selectionStart);
      const afterCursor = val.substring(selectionStart);
      
      const replacedBefore = beforeCursor.replace(/--/g, '—');
      const replacedAfter = afterCursor.replace(/--/g, '—');
      
      const newVal = replacedBefore + replacedAfter;
      const diff = beforeCursor.length - replacedBefore.length;
      const newSelectionStart = selectionStart - diff;
      
      setText(newVal);
      
      requestAnimationFrame(() => {
        if (target) {
          target.setSelectionRange(newSelectionStart, newSelectionStart);
        }
      });
    } else {
      setText(val);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Backdrop with stronger blur for focus */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-md" onClick={onClose} />
      
      {/* Modal - Soft Rectangular Card */}
      <div 
        className={`relative w-full max-w-lg shadow-2xl rounded-md animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto ${
        isDarkMode ? 'bg-stone-900 border border-stone-800 text-white' : 'bg-white border border-stone-100 text-stone-900'
      } p-6 sm:p-10 md:p-12`}
      >
        
        <button onClick={onClose} className="absolute top-6 right-6 md:top-8 md:right-8 opacity-40 hover:opacity-100 transition-opacity">
          <X size={20} />
        </button>

        <div className="flex items-center space-x-4 mb-8 md:mb-10">
          <div 
            className={`p-3 rounded ${isDarkMode ? 'bg-stone-800 text-stone-300' : 'bg-stone-50 text-stone-900'}`}
          >
            <Feather size={20} />
          </div>
          <h3 className="text-xl md:text-2xl font-serif">
            {mode === 'edit' ? 'Edit Poem' : 'Add Poem'}
          </h3>
        </div>

        <div className="space-y-6">
          {/* Poem Title Input */}
          <div className="space-y-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-2">
              Poem Title
            </label>
            <input
              type="text"
              placeholder="e.g., Echoes of Twilight (Optional)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={`w-full px-6 py-4 border outline-none transition-all text-base rounded-sm ${
                isDarkMode 
                  ? 'bg-stone-950 border-stone-800 focus:border-stone-600' 
                  : 'bg-stone-50 border-stone-100 focus:border-stone-300'
              }`}
            />
          </div>

          {/* Poem Text/Verse Input */}
          <div className="space-y-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-2">
              The Verses
            </label>
            <textarea
              ref={textareaRef}
              placeholder="Type the poem stanzas here..."
              value={text}
              onChange={handleTextChange}
              className={`w-full px-6 py-5 border outline-none transition-all text-base min-h-[160px] resize-none overflow-hidden rounded-sm font-serif leading-relaxed ${
                isDarkMode 
                  ? 'bg-stone-950 border-stone-800 focus:border-stone-600' 
                  : 'bg-stone-50 border-stone-100 focus:border-stone-300'
              }`}
              style={{ whiteSpace: 'pre-wrap' }}
            />
          </div>

          {/* Author Input */}
          <div className="space-y-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-2">
              Attribution
            </label>
            <input
              type="text"
              placeholder="e.g., Ben Hasan"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className={`w-full px-6 py-4 border outline-none transition-all text-base rounded-sm ${
                isDarkMode 
                  ? 'bg-stone-950 border-stone-800 focus:border-stone-600' 
                  : 'bg-stone-50 border-stone-100 focus:border-stone-300'
              }`}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {/* Date Input */}
            <div className="space-y-3 flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-2">
                Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full px-6 py-4 border outline-none transition-all text-base rounded-sm ${
                  isDarkMode 
                    ? 'bg-stone-950 border-stone-800 focus:border-stone-600' 
                    : 'bg-stone-50 border-stone-100 focus:border-stone-300'
                }`}
              />
            </div>

            {/* Tags Input */}
            <div className="space-y-3 flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-[0.3em] opacity-40 px-2">
                Tags
              </label>
              <input
                type="text"
                placeholder="Melancholy, Life"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className={`w-full px-6 py-4 border outline-none transition-all text-base rounded-sm ${
                  isDarkMode 
                    ? 'bg-stone-950 border-stone-800 focus:border-stone-600' 
                    : 'bg-stone-50 border-stone-100 focus:border-stone-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons - Soft Rectangles */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mt-8 md:mt-12">
          <button
            onClick={onClose}
            className={`w-full sm:flex-1 py-4 rounded-sm text-[10px] font-bold uppercase tracking-widest transition-all border ${
              isDarkMode 
                ? 'border-stone-800 text-stone-500 hover:bg-stone-800 hover:text-stone-200' 
                : 'border-stone-100 text-stone-400 hover:bg-stone-50 hover:text-stone-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={isSaving}
            className="w-full sm:flex-1 py-4 rounded-sm bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-[10px] font-bold uppercase tracking-widest hover:opacity-80 transition-all flex items-center justify-center space-x-3 shadow-xl"
          >
            {isSaving ? <Loader2 className="animate-spin" size={16} /> : <span>{mode === 'edit' ? 'Update' : 'Confirm'}</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PoemEditorModal;
